import type { Feedback, GeologicalReview, JobStatus, Recommendation, Well } from './types';
import { supabase } from './supabase';

const apiBaseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, '');
const ACTIVE_TENANT_STORAGE_KEY = 'welldone.activeTenantId';

export type TenantRole = 'member' | 'tenant_admin';
export type AccessTenant = { id: string; name: string; slug: string; role: TenantRole; support_context?: boolean };
export type AccessProfile = { id: string; email: string; display_name: string | null; super_admin: boolean };
export type AccessState = { profile: AccessProfile | null; super_admin: boolean; tenants: AccessTenant[] };
export type TeamMember = { id: string; tenant_id: string; user_id: string; role: TenantRole; created_at: string; profile: AccessProfile | null };
export type PlatformTenant = { id: string; name: string; slug: string; created_at: string; members: number; admins: number; subscription: { stripe_status: string; seat_quantity: number; current_period_end: string | null } | null };
export type AdminHealth = { queued_jobs: number; tenant_count: number; workers: Array<{ worker_id: string; last_seen_at: string }> };
export type OperationalAvailability = {
  inference_node_online: boolean;
  inference_node_state?: 'online' | 'working' | 'unavailable';
};

export const getActiveTenantId = () => window.localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY);
export const setActiveTenantId = (tenantId: string | null) => {
  if (tenantId) window.localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, tenantId);
  else window.localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
};

async function sessionHeaders(): Promise<Record<string, string>> {
  if (!supabase) return {};
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session?.access_token) throw new Error('Authentication required.');
  return { Authorization: `Bearer ${sessionData.session.access_token}` };
}

async function requestHeaders(tenantId?: string | null): Promise<Record<string, string>> {
  const headers = await sessionHeaders();
  const selectedTenantId = tenantId ?? getActiveTenantId();
  if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
  return headers;
}

async function request<T>(path: string, init?: RequestInit, tenantId?: string | null): Promise<T> {
  if (!apiBaseUrl) throw new Error('API endpoint is not configured.');
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(await requestHeaders(tenantId)), ...(init?.headers ?? {}) },
  });
  if (!response.ok) throw new Error((await response.json().catch(() => null))?.error ?? 'Request failed.');
  return response.json() as Promise<T>;
}

export async function getAccess(): Promise<AccessState> {
  if (!apiBaseUrl) return { profile: null, super_admin: false, tenants: [] };
  return request<AccessState>('/v1/access', undefined, null);
}

export type OrganisationOnboardingResult = { provisioned: boolean; tenant?: { id: string; name: string; slug: string } };

export async function completeOrganisationOnboarding(): Promise<OrganisationOnboardingResult> {
  return request<OrganisationOnboardingResult>('/v1/onboarding/organisation', { method: 'POST', body: '{}' }, null);
}

export type ContactEnquiry = {
  name: string;
  email: string;
  organisation: string;
  role?: 'Exploration manager' | 'Geologist' | 'Technical director' | 'Wireline or petrophysics specialist' | 'Other';
  context: string;
  website?: string;
};

export async function submitContactEnquiry(input: ContactEnquiry): Promise<void> {
  if (!apiBaseUrl) throw new Error('Contact delivery is not configured.');
  const response = await fetch(`${apiBaseUrl}/v1/contact`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
  if (!response.ok) throw new Error((await response.json().catch(() => null))?.error ?? 'Unable to send your request right now.');
}

export async function acceptInvite(token: string): Promise<{ tenant_id: string; seat_quantity: number }> {
  return request('/v1/invites/accept', { method: 'POST', body: JSON.stringify({ token }) }, null);
}

export async function getWells(): Promise<Well[]> {
  if (!apiBaseUrl) return [];
  return request<Well[]>('/v1/wells');
}

export async function getJobs(): Promise<JobStatus[]> {
  if (!apiBaseUrl) return [];
  return request<JobStatus[]>('/v1/jobs');
}

export async function submitFeedback(recommendationId: string, feedback: Feedback): Promise<void> {
  if (!apiBaseUrl) return;
  await request(`/v1/recommendations/${recommendationId}/feedback`, { method: 'POST', body: JSON.stringify(feedback) });
}

export async function submitGeologicalReview(recommendationId: string, review: Omit<GeologicalReview, 'created_at'>): Promise<GeologicalReview> {
  if (!apiBaseUrl) return { ...review, created_at: new Date().toISOString() };
  return request<GeologicalReview>(`/v1/recommendations/${recommendationId}/geological-review`, { method: 'POST', body: JSON.stringify(review) });
}

export type TabularLogMapping = {
  delimiter: ',' | '\t' | ';';
  columns: { depth_m: string; natural_gamma: string; resistivity: string; spontaneous_potential: string; caliper: string };
  units: { depth_m: 'm' | 'ft'; natural_gamma: 'API' | 'GAPI'; resistivity: 'ohm.m'; spontaneous_potential: 'mV' | 'V'; caliper: 'in' | 'mm' };
};

export async function submitUpload(file: File, wellName: string, tabularMapping?: TabularLogMapping): Promise<{ job_id: string }> {
  if (!apiBaseUrl) throw new Error('The production upload service is unavailable.');
  const formData = new FormData();
  formData.set('file', file);
  formData.set('well_name', wellName);
  if (tabularMapping) formData.set('tabular_mapping', JSON.stringify(tabularMapping));
  const response = await fetch(`${apiBaseUrl}/v1/uploads`, { method: 'POST', headers: await requestHeaders(), body: formData });
  if (!response.ok) throw new Error((await response.json().catch(() => null))?.error ?? 'Upload failed.');
  return response.json() as Promise<{ job_id: string }>;
}

export async function beginCheckout(plan: 'standard' | 'pro' = 'standard'): Promise<{ url: string }> {
  if (!apiBaseUrl) throw new Error('Billing is not configured in demo mode.');
  return request('/v1/billing/checkout', { method: 'POST', body: JSON.stringify({ plan }) });
}

export async function createRecommendationRationale(input: Pick<Recommendation, 'drivers' | 'confidence_score'>): Promise<string> {
  if (!apiBaseUrl) return `Confidence is ${input.confidence_score}% based on ${input.drivers.join(', ').toLowerCase()}.`;
  const output = await request<{ rationale: string }>('/v1/recommendations/rationale', { method: 'POST', body: JSON.stringify(input) });
  return output.rationale;
}

export async function openBillingPortal(): Promise<{ url: string }> {
  return request('/v1/billing/portal', { method: 'POST' });
}

export async function listTenantMembers(tenantId: string): Promise<TeamMember[]> {
  return request(`/v1/tenants/${tenantId}/members`, undefined, tenantId);
}
export async function inviteTenantMember(tenantId: string, input: { email: string; role: TenantRole }) {
  return request(`/v1/tenants/${tenantId}/invites`, { method: 'POST', body: JSON.stringify(input) }, tenantId);
}
export async function updateTenantMemberRole(tenantId: string, userId: string, role: TenantRole) {
  return request(`/v1/tenants/${tenantId}/members/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) }, tenantId);
}
export async function removeTenantMember(tenantId: string, userId: string) {
  return request(`/v1/tenants/${tenantId}/members/${userId}`, { method: 'DELETE' }, tenantId);
}
export async function listPlatformTenants(): Promise<PlatformTenant[]> {
  return request('/v1/admin/tenants', undefined, null);
}
export async function createPlatformTenant(input: { name: string; slug: string; initial_admin_email: string }) {
  return request('/v1/admin/tenants', { method: 'POST', body: JSON.stringify(input) }, null);
}
export async function updatePlatformTenant(tenantId: string, input: { name?: string; slug?: string }) {
  return request(`/v1/admin/tenants/${tenantId}`, { method: 'PATCH', body: JSON.stringify(input) }, null);
}
export async function listSuperAdmins(): Promise<AccessProfile[]> {
  return request('/v1/admin/super-admins', undefined, null);
}
export async function grantSuperAdmin(email: string): Promise<AccessProfile> {
  return request('/v1/admin/super-admins', { method: 'POST', body: JSON.stringify({ email }) }, null);
}
export async function setSuperAdmin(userId: string, superAdmin: boolean): Promise<AccessProfile> {
  return request(`/v1/admin/super-admins/${userId}`, { method: 'PATCH', body: JSON.stringify({ super_admin: superAdmin }) }, null);
}
export async function getAdminHealth(): Promise<AdminHealth> {
  return request('/v1/admin/health', undefined, null);
}

export async function getOperationalAvailability(): Promise<OperationalAvailability> {
  return request('/v1/operations/status');
}
